// Records organized as one folder per month, grouped by year: used by
// Operaciones, Avisos and Bitácora. A folder links to that month's page.
import { addMonthsToMonth, formatMonthName, type IsoMonth, yearOf } from "@pcj/shared";
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Folder, FolderOpen } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { capitalizeFirst, monthTitle } from "../lib/format";

export interface MonthFolder {
  month: IsoMonth;
  /** Main line, e.g. "12 operaciones". */
  title: string;
  /** Second line, e.g. an amount. */
  detail?: string;
  /** Status pills. */
  badges?: ReactNode;
  /** Nothing inside: drawn muted. */
  empty?: boolean;
}

/** Folders by year: the newest year first and open, months in calendar order. */
export function MonthFolders({
  folders,
  currentMonth,
  yearSummary,
}: {
  folders: readonly MonthFolder[];
  currentMonth: IsoMonth;
  /** Shown next to each year, e.g. its totals. */
  yearSummary?: (year: number, folders: readonly MonthFolder[]) => ReactNode;
}) {
  const years = [...new Set(folders.map((f) => yearOf(f.month)))].sort((a, b) => b - a);
  return (
    <div className="space-y-3">
      {years.map((year, index) => {
        const yearFolders = folders
          .filter((f) => yearOf(f.month) === year)
          .sort((a, b) => a.month.localeCompare(b.month));
        return (
          <YearSection
            key={year}
            year={year}
            folders={yearFolders}
            currentMonth={currentMonth}
            summary={yearSummary?.(year, yearFolders)}
            defaultOpen={index === 0}
          />
        );
      })}
    </div>
  );
}

function YearSection({
  year,
  folders,
  currentMonth,
  summary,
  defaultOpen,
}: {
  year: number;
  folders: readonly MonthFolder[];
  currentMonth: IsoMonth;
  summary: ReactNode;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left hover:bg-gray-50"
      >
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-base font-semibold text-gray-900">{year}</span>
          {summary && <span className="text-sm text-gray-500">{summary}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <ul className="grid grid-cols-2 gap-3 border-t border-gray-100 p-4 sm:grid-cols-3 lg:grid-cols-4">
          {folders.map((folder) => {
            const current = folder.month === currentMonth;
            const Icon = folder.empty ? Folder : FolderOpen;
            return (
              <li key={folder.month}>
                <Link
                  to={folder.month}
                  className={`flex h-full flex-col rounded-lg border p-3 transition hover:border-gold-500 hover:shadow-sm ${
                    current ? "border-gold-300 bg-cream" : "border-gray-200 bg-white"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon
                      className={`h-5 w-5 shrink-0 ${folder.empty ? "text-gray-300" : "text-gold-500"}`}
                      aria-hidden
                    />
                    <span className="font-medium text-gray-900">{capitalizeFirst(formatMonthName(folder.month))}</span>
                    {current && <span className="text-[11px] text-gold-700">· en curso</span>}
                  </span>
                  <span className={`mt-1 text-sm ${folder.empty ? "text-gray-400" : "text-gray-700"}`}>
                    {folder.title}
                  </span>
                  {folder.detail && <span className="text-xs text-gray-500">{folder.detail}</span>}
                  {folder.badges && <span className="mt-2 flex flex-wrap gap-1">{folder.badges}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Top of a month's page: back to the folders, the month, links to the
 * months before and after (when they exist) and the page's actions.
 */
export function MonthHeader({
  month,
  title,
  subtitle,
  backLabel = "Todos los meses",
  first,
  last,
  actions,
}: {
  month: IsoMonth;
  /** e.g. "Operaciones de"; the month follows. */
  title: string;
  subtitle?: string;
  backLabel?: string;
  /** Oldest and newest months that can be navigated to; omit to hide the arrows. */
  first?: IsoMonth;
  last?: IsoMonth;
  actions?: ReactNode;
}) {
  const previous = addMonthsToMonth(month, -1);
  const next = addMonthsToMonth(month, 1);
  const arrow = "rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600 hover:bg-gray-50";
  return (
    <div className="space-y-2">
      <Link
        to=".."
        relative="path"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> {backLabel}
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          {first && previous >= first && (
            <Link to={`../${previous}`} relative="path" className={arrow} aria-label="Mes anterior">
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Link>
          )}
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {title} {monthTitle(month).toLowerCase()}
            </h2>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          {last && next <= last && (
            <Link to={`../${next}`} relative="path" className={arrow} aria-label="Mes siguiente">
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          )}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}
